import { Injectable } from '@angular/core';
import { map, Observable, shareReplay, timer } from 'rxjs';

export interface SalesCountdown {
  /** Milisegundos que quedan, por si hace falta decidir con cuánta antelación avisar */
  total: number;
  days: number;
  /** Ya formateados y con cero delante, que es como se pintan */
  dd: string;
  hh: string;
  mm: string;
  ss: string;
  /**
   * Qué unidades merece la pena pintar: siempre las tres más significativas
   * que no estén a cero, para no acabar en un "00h 48m 07s" ni en un "03d 12h
   * 44m 09s" donde los segundos no dicen nada.
   */
  showDays: boolean;
  showHours: boolean;
  showMinutes: boolean;
  showSeconds: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class SalesCountdownService {

  /**
   * Un único reloj para todas las cards. Si cada una montara su propio
   * setInterval, un listado de 50 eventos dispararía 50 detecciones de cambios
   * por segundo; así solo hay una.
   */
  private readonly tick$ = timer(0, 1000).pipe(
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /**
   * Cuenta atrás hasta la fecha dada, o null cuando ya ha pasado. La fecha se
   * interpreta igual que en el botón de compra del detalle (
   * open-external.component.ts), para que la card no diga "Próximamente"
   * cuando la ficha ya deja comprar.
   */
  countdownTo(date: string | Date): Observable<SalesCountdown | null> {
    const target = new Date(date).getTime();
    return this.tick$.pipe(map(() => this.remainingTo(target)));
  }

  remainingTo(target: number): SalesCountdown | null {
    const total = target - Date.now();
    if (!target || isNaN(target) || total <= 0) {
      return null;
    }
    const seconds = Math.floor(total / 1000);
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return {
      total,
      days,
      dd: String(days).padStart(2, '0'),
      hh: String(hours).padStart(2, '0'),
      mm: String(minutes).padStart(2, '0'),
      ss: String(seconds % 60).padStart(2, '0'),
      showDays: days > 0,
      showHours: days > 0 || hours > 0,
      showMinutes: days > 0 || hours > 0 || minutes > 0,
      showSeconds: days === 0
    };
  }
}
